import { Component, inject, PLATFORM_ID, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Navbar } from '../../shared/components/layout/navbar/navbar';
import { NgIcon } from '@ng-icons/core';
import { ButtonModule } from 'primeng/button';
import { Footer } from '../../shared/components/layout/footer/footer';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { SeoManager } from '../../core/services/seo/seo-manager';

@Component({
  selector: 'app-features',
  imports: [Navbar, NgIcon, ButtonModule, Footer],
  templateUrl: './features.html',
  styleUrl: './features.scss',
})
export class Features implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private scrollSmoother: ScrollSmoother | null = null;
  private seoManager = inject(SeoManager);

  @ViewChild('smoothWrapper', { static: false }) smoothWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('smoothContent', { static: false }) smoothContent!: ElementRef<HTMLDivElement>;
  @ViewChild('heroSection', { static: false }) heroSection!: ElementRef<HTMLElement>;
  @ViewChild('heroTitle', { static: false }) heroTitle!: ElementRef<HTMLHeadingElement>;
  @ViewChild('heroParagraph', { static: false }) heroParagraph!: ElementRef<HTMLParagraphElement>;
  @ViewChild('heroButton', { static: false }) heroButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('heroImage', { static: false }) heroImage!: ElementRef<HTMLImageElement>;
  @ViewChild('benefitsSection', { static: false }) benefitsSection!: ElementRef<HTMLElement>;
  @ViewChild('stickySidebar', { static: false }) stickySidebar!: ElementRef<HTMLDivElement>;
  @ViewChild('benefitsTitle', { static: false }) benefitsTitle!: ElementRef<HTMLHeadingElement>;
  @ViewChild('benefitsList', { static: false }) benefitsList!: ElementRef<HTMLUListElement>;
  @ViewChild('indicatorsTitle', { static: false }) indicatorsTitle!: ElementRef<HTMLHeadingElement>;
  @ViewChild('card1', { static: false }) card1!: ElementRef<HTMLDivElement>;
  @ViewChild('card2', { static: false }) card2!: ElementRef<HTMLDivElement>;
  @ViewChild('card3', { static: false }) card3!: ElementRef<HTMLDivElement>;
  @ViewChild('whySection', { static: false }) whySection!: ElementRef<HTMLElement>;
  @ViewChild('whyTitle', { static: false }) whyTitle!: ElementRef<HTMLHeadingElement>;
  @ViewChild('whyContent', { static: false }) whyContent!: ElementRef<HTMLDivElement>;


  constructor() {
    this.seoManager.updateSeoTags({
      title: 'Producto',
      description: 'Descubra los beneficios de nuestro producto.',
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
      this.initScrollSmoother();
      this.initAnimations();
      this.initScrollAnimations();
    }
  }

  ngOnDestroy() {
    if (this.scrollSmoother) {
      this.scrollSmoother.kill();
    }
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }

  private initScrollSmoother() {
    if (!this.smoothWrapper?.nativeElement || !this.smoothContent?.nativeElement) {
      return;
    }

    this.scrollSmoother = ScrollSmoother.create({
      wrapper: this.smoothWrapper.nativeElement,
      content: this.smoothContent.nativeElement,
      smooth: 1.5,
      effects: true,
      smoothTouch: 0.1,
    });
  }

  private initAnimations() {
    if (!this.heroTitle?.nativeElement || !this.heroParagraph?.nativeElement || 
        !this.heroButton?.nativeElement || !this.heroImage?.nativeElement) {
      return;
    }

    const tl = gsap.timeline();

    tl.to(this.heroTitle.nativeElement, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out'
    })
    .to(this.heroParagraph.nativeElement, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out'
    }, '-=0.4')
    .to(this.heroButton.nativeElement, {
      opacity: 1,
      duration: 0.6,
      ease: 'power2.out'
    }, '-=0.2')
    .to(this.heroImage.nativeElement, {
      opacity: 1,
      scale: 1,
      duration: 1,
      ease: 'power3.out'
    }, '-=0.4');
  }

  private initScrollAnimations() {
    // Animación para el título de indicadores
    if (this.indicatorsTitle?.nativeElement) {
      gsap.to(this.indicatorsTitle.nativeElement, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: this.indicatorsTitle.nativeElement,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none none'
        }
      });
    }

    // Animaciones para las tarjetas sticky con delay
    const cards = [this.card1, this.card2, this.card3];
    cards.forEach((card, index) => {
      if (card?.nativeElement) {
        gsap.to(card.nativeElement, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          delay: index * 0.2,
          scrollTrigger: {
            trigger: card.nativeElement,
            start: 'top 85%',
            end: 'bottom 20%',
            toggleActions: 'play none none none'
          }
        });
      }
    });

    // Animación para la sección "¿Por qué nosotros?"
    if (this.whyContent?.nativeElement) {
      gsap.to(this.whyContent.nativeElement, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: this.whyContent.nativeElement,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none none'
        }
      });
    }

    // Arreglar el sticky para que funcione con ScrollSmoother
    // ScrollSmoother puede interferir con sticky, así que usamos ScrollTrigger para simularlo
    if (this.benefitsTitle?.nativeElement && this.benefitsList?.nativeElement && this.benefitsSection?.nativeElement && this.stickySidebar?.nativeElement) {
      const section = this.benefitsSection.nativeElement;
      const cardsContainer = section.querySelector('.relative.flex.flex-col');
      
      if (cardsContainer) {
        ScrollTrigger.create({
          trigger: cardsContainer,
          start: 'top 40%',
          end: 'bottom bottom',
          pin: this.stickySidebar.nativeElement,
          pinSpacing: false,
          invalidateOnRefresh: true
        });
      }
    }
  }

  quote() {
    this.router.navigate(['contact']);
  }
}
